/**
 * SignoffReportsPanel — Phase-3 step 2 (final). The ADMIN records a resolution
 * per finding then signs off (checker step, PIN-gated). On sign-off the audit
 * locks, alerts fire (Telegram best-effort) and role-based report packets +
 * a read-only share link become available.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../contexts/AuthContext';
import { formatPHP } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Loader2, Gavel, CheckCircle2, Lock, Copy, Share2, FileSpreadsheet, Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import AuthDialog from '../../AuthDialog';
import InvestigationQueue from '../InvestigationQueue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RES_OPTIONS = [
  { key: 'investigate', label: 'Investigate' },
  { key: 'write_off', label: 'Write off' },
  { key: 'adjust', label: 'Adjust books' },
  { key: 'chase', label: 'Chase / collect' },
  { key: 'accept', label: 'Accept (no action)' },
];

export default function SignoffReportsPanel({ session, onSignedOff, onJump }) {
  const completed = session.status === 'completed';
  const reviewDone = session.steps?.review?.status === 'complete' || session.mode === 'partial';
  const findings = session.reconcile_result?.findings || [];

  const [res, setRes] = useState({});         // finding_id -> {resolution, note}
  const [authOpen, setAuthOpen] = useState(false);
  const [signing, setSigning] = useState(false);

  // Report state (post sign-off)
  const [role, setRole] = useState('owner');
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [share, setShare] = useState(session.share_token ? { share_token: session.share_token } : null);
  const [sharing, setSharing] = useState(false);

  const loadReport = useCallback(async (r) => {
    setLoadingReport(true);
    try {
      const out = await api.get(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/report?role=${r}`);
      setReport(out.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load report'); }
    setLoadingReport(false);
  }, [session.id]);

  useEffect(() => { if (completed) loadReport(role); }, [completed, role, loadReport]);

  const setResolution = (id, field, v) => setRes((p) => ({ ...p, [id]: { ...p[id], [field]: v } }));

  const doSignOff = async () => {
    setSigning(true);
    try {
      const resolutions = findings.map((f) => ({
        finding_id: f.id, title: f.title,
        resolution: res[f.id]?.resolution || 'investigate',
        note: res[f.id]?.note || '',
      }));
      const out = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/sign-off`, { resolutions });
      toast.success('Audit signed off');
      onSignedOff?.(out.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not sign off');
    }
    setSigning(false);
  };

  const createShare = async () => {
    setSharing(true);
    try {
      const out = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/share`, { expires_days: 14 });
      setShare(out.data);
      toast.success('Share link created');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create link'); }
    setSharing(false);
  };

  const shareUrl = share ? `${window.location.origin}/audit-report/${share.share_token}` : '';

  if (!reviewDone && !completed) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" data-testid="signoff-locked">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3"><Lock size={20} className="text-slate-400" /></div>
        <p className="text-sm font-semibold text-slate-700">Auditor approval required</p>
        <p className="text-xs text-slate-500 max-w-sm mt-1">The auditor must approve the count in Review before the admin can sign off.</p>
        <button onClick={() => onJump?.('review')} className="mt-3 text-sm font-semibold text-[#1A4D2E] hover:underline" data-testid="signoff-goto-review">Go to Review →</button>
      </div>
    );
  }

  /* ── Post sign-off: role-based reports + share ── */
  if (completed) {
    const m = report?.meta || {};
    const sc = report?.scores || {};
    return (
      <div className="space-y-4" data-testid="signoff-complete">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
          <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">Audit signed off</p>
            <p className="text-xs text-emerald-700 mt-0.5">{session.signoff_summary} · by {session.signed_off_by}. {session.notified?.telegram ? `${session.notified.telegram} Telegram alert(s) queued.` : ''}</p>
          </div>
        </div>

        {/* Role packet tabs */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-1.5 flex-wrap">
            {(report?.roles || [{ key: 'owner', label: 'Owner' }]).map((r) => (
              <button key={r.key} onClick={() => setRole(r.key)} data-testid={`report-role-${r.key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${role === r.key ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-slate-500 border-slate-200'}`}>{r.label}</button>
            ))}
          </div>
          <button onClick={() => window.print()} data-testid="report-print" className="text-xs font-semibold text-slate-500 hover:text-slate-700 flex items-center gap-1"><Printer size={13} />Print</button>
        </div>

        {loadingReport ? (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 size={18} className="animate-spin" /></div>
        ) : report ? (
          <div className="space-y-3" data-testid="report-body">
            <p className="text-xs text-slate-500">{report.role_blurb}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Health" value={`${sc.health_score ?? '—'}`} />
              <Stat label="Risk" value={`${sc.fraud_risk_score ?? '—'}`} />
              <Stat label="Cash variance" value={formatPHP(report.cash?.physical_variance ?? report.cash?.discrepancy ?? 0)} />
              <Stat label="Stock net" value={report.inventory?.available ? formatPHP(report.inventory?.summary?.total_variance_capital ?? 0) : 'n/a'} />
            </div>
            <InvestigationQueue auditData={{ findings: report.findings, findings_summary: report.findings_summary }} />
            {(report.resolutions || []).length > 0 && (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wide text-slate-400">Resolutions</div>
                <div className="divide-y divide-slate-50">
                  {report.resolutions.map((r, i) => (
                    <div key={i} className="px-4 py-2 flex items-center justify-between gap-3 text-xs">
                      <span className="text-slate-600 truncate flex-1">{r.title}</span>
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold capitalize shrink-0">{r.resolution.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Share link */}
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2"><Share2 size={15} className="text-[#1A4D2E]" /><span className="text-sm font-semibold text-slate-700">Read-only share link</span></div>
            {!share ? (
              <Button onClick={createShare} disabled={sharing} data-testid="audit-share-link" className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-9 px-4 text-xs">
                {sharing ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Share2 size={13} className="mr-1.5" />}Create link
              </Button>
            ) : null}
          </div>
          {share && (
            <div className="mt-2 flex items-center gap-2">
              <Input readOnly value={shareUrl} className="h-9 text-xs font-mono" data-testid="audit-share-url" />
              <Button onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success('Copied'); }} variant="outline" className="h-9 px-3" data-testid="audit-share-copy"><Copy size={14} /></Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Pre sign-off: resolutions + sign-off ── */
  return (
    <div className="space-y-4" data-testid="signoff-panel">
      <p className="text-xs text-slate-500">Decide what happens to each finding, then sign off. Sign-off locks the audit and chains this count as the next baseline.</p>
      {findings.length === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 size={16} />No findings to resolve — this audit is clean.</div>
      ) : (
        <div className="rounded-lg border border-slate-200 overflow-hidden max-h-[360px] overflow-y-auto">
          {findings.map((f) => (
            <div key={f.id} className="px-4 py-3 border-b border-slate-50 last:border-0" data-testid={`signoff-finding-${f.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-sm font-medium text-slate-700 truncate flex-1">{f.title}</p>
                {f.amount_impact ? <span className={`font-mono text-xs font-semibold shrink-0 ${f.amount_impact < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatPHP(f.amount_impact)}</span> : null}
              </div>
              <div className="flex gap-2">
                <Select value={res[f.id]?.resolution || 'investigate'} onValueChange={(v) => setResolution(f.id, 'resolution', v)}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid={`signoff-resolution-${f.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{RES_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Note (optional)" value={res[f.id]?.note || ''} onChange={(e) => setResolution(f.id, 'note', e.target.value)}
                  className="h-8 text-xs flex-1" data-testid={`signoff-note-${f.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end pt-2 border-t border-slate-100">
        <Button onClick={() => setAuthOpen(true)} disabled={signing} data-testid="signoff-confirm"
          className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 px-6">
          {signing ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Gavel size={15} className="mr-1.5" />}Admin: Sign Off
        </Button>
      </div>

      <AuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        mode="either"
        context="audit_sign_off"
        title="Sign Off Audit"
        description="The admin confirms the resolutions and finalizes the audit. This locks the session."
        onVerified={() => { setAuthOpen(false); doSignOff(); }}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-center">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm font-bold text-slate-700 font-mono">{value}</p>
    </div>
  );
}
